import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, Text, Button, Input, Select, Badge, Table, Thead, Tbody, Tr, Th, Td,
  VStack, HStack, FormControl, FormLabel, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, useToast,
  Tabs, TabList, Tab, TabPanels, TabPanel, Stat, StatLabel, StatNumber, StatHelpText,
  SimpleGrid, Divider, IconButton, Switch, Textarea, InputGroup, InputRightElement,
  Alert, AlertIcon, Code, NumberInput, NumberInputField, Spinner,
} from '@chakra-ui/react';
import { FiShield, FiUsers, FiActivity, FiPlus, FiEdit2, FiRefreshCw,
         FiLogIn, FiAlertTriangle, FiPause, FiPlay, FiKey, FiSearch, FiEye,
         FiSmartphone, FiCheckCircle, FiLock, FiMail, FiSend, FiSave, FiX,
         FiGrid, FiTrash2, FiChevronDown, FiChevronUp, FiCopy, FiTerminal, FiSettings, FiDownload } from 'react-icons/fi';
import axios from 'axios';

const API = (axios.defaults.baseURL || '') + '/api/t-admin';

function tadminApi(token) {
  const h = { Authorization: `Bearer ${token}` };
  return {
    get:    (path, params) => axios.get(`${API}${path}`,    { headers: h, params }),
    post:   (path, data)   => axios.post(`${API}${path}`,   data, { headers: h }),
    put:    (path, data)   => axios.put(`${API}${path}`,    data, { headers: h }),
    delete: (path, data)   => axios.delete(`${API}${path}`, { headers: h, data }),
  };
}

// ── Login screen ──────────────────────────────────────────────────────────────
function TAdminLogin({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupMode, setSetupMode] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [setupAvailable, setSetupAvailable] = useState(false); // only show if no admins exist
  const toast = useToast();

  // Check on mount whether first-time setup is available
  useEffect(() => {
    axios.get(`${API}/setup-status`)
      .then(res => setSetupAvailable(!!res.data?.setup_needed))
      .catch(() => setSetupAvailable(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const payload = { username, password };
      if (totpRequired) payload.totp_code = totpCode;
      const { data } = await axios.post(`${API}/login`, payload);
      // axios resolves 206 successfully — check for totp_required in the response
      if (data.totp_required) {
        setTotpRequired(true); setError('');
      } else {
        onLogin(data.token, data.admin);
      }
    } catch(err) {
      // 4xx/5xx errors land here
      if (err.response?.status === 206 && err.response.data?.totp_required) {
        // Fallback in case axios does throw on 206 in some configurations
        setTotpRequired(true); setError('');
      } else {
        setError(err.response?.data?.error || 'Login failed');
        if (err.response?.data?.totp_failed) setTotpCode('');
      }
    } finally { setLoading(false); }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/setup`, { username, email: setupEmail, password });
      toast({ title: 'Super-admin created — please log in', status: 'success', duration: 3000 });
      setSetupMode(false);
    } catch(err) {
      setError(err.response?.data?.error || 'Setup failed');
    } finally { setLoading(false); }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.900">
      <Box bg="gray.800" p={8} borderRadius="xl" w="380px" boxShadow="2xl">
        <HStack mb={6} spacing={2}>
          <FiShield size={24} color="#63b3ed" />
          <Text fontSize="20px" fontWeight="800" color="white">ForgeBOS T-Admin</Text>
        </HStack>
        <Text fontSize="12px" color="gray.400" mb={6}>
          {setupMode ? 'Create your first super-admin account' : totpRequired ? 'Enter your authenticator code' : 'Super-admin access only'}
        </Text>
        {error && <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{error}</Alert>}
        <form onSubmit={setupMode ? handleSetup : handleLogin}>
          <VStack spacing={3}>
            {!totpRequired ? (
              <>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.400">Username</FormLabel>
                  <Input bg="gray.700" border="none" color="white" value={username}
                    onChange={e => setUsername(e.target.value)} autoFocus />
                </FormControl>
                {setupMode && (
                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.400">Email</FormLabel>
                    <Input bg="gray.700" border="none" color="white" type="email" value={setupEmail}
                      onChange={e => setSetupEmail(e.target.value)} />
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.400">Password</FormLabel>
                  <Input bg="gray.700" border="none" color="white" type="password" value={password}
                    onChange={e => setPassword(e.target.value)} />
                </FormControl>
              </>
            ) : (
              <FormControl>
                <FormLabel fontSize="xs" color="gray.400">6-digit authenticator code</FormLabel>
                <Input bg="gray.700" border="none" color="white" fontSize="24px" textAlign="center"
                  letterSpacing="0.3em" maxLength={6} value={totpCode} autoFocus
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0,6))}
                  placeholder="000000" />
                <Text fontSize="10px" color="gray.500" mt={1}>
                  Open Google Authenticator and enter the 6-digit code for ForgeBOS T-Admin
                </Text>
              </FormControl>
            )}
            <Button type="submit" colorScheme="blue" w="100%" isLoading={loading}
              isDisabled={totpRequired && totpCode.length !== 6}>
              {setupMode ? 'Create Super-Admin' : totpRequired ? 'Verify Code' : 'Login'}
            </Button>
            {totpRequired ? (
              <Button variant="ghost" size="sm" color="gray.500"
                onClick={() => { setTotpRequired(false); setTotpCode(''); setError(''); }}>
                ← Back to password
              </Button>
            ) : setupAvailable && (
              <Button variant="ghost" size="sm" color="gray.500" onClick={() => setSetupMode(p => !p)}>
                {setupMode ? 'Back to login' : 'First time setup'}
              </Button>
            )}
          </VStack>
        </form>
      </Box>
    </Flex>
  );
}

// ── Dashboard cards ───────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color = 'blue' }) {
  return (
    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
      <Flex justify="space-between" align="flex-start">
        <Stat>
          <StatLabel fontSize="11px" color="gray.500" textTransform="uppercase">{label}</StatLabel>
          <StatNumber fontSize="28px" fontWeight="800" color={`${color}.600`}>{value}</StatNumber>
          {sub && <StatHelpText fontSize="11px">{sub}</StatHelpText>}
        </Stat>
        {Icon && <Box color={`${color}.400`} mt={1}><Icon size={22} /></Box>}
      </Flex>
    </Box>
  );
}

// ── Activity log row ──────────────────────────────────────────────────────────
function eventColor(type) {
  if (type === 'login_success') return 'green';
  if (type === 'login_fail') return 'red';
  if (type === 'password_reset') return 'orange';
  return 'gray';
}

function ActivityTable({ rows }) {
  return (
    <Box overflowX="auto">
      <Table size="sm">
        <Thead><Tr>
          <Th>Time</Th><Th>Tenant</Th><Th>User</Th><Th>Event</Th><Th>IP</Th><Th>Details</Th>
        </Tr></Thead>
        <Tbody>
          {rows.map(r => (
            <Tr key={r.id} _hover={{ bg: 'gray.50' }}>
              <Td whiteSpace="nowrap" fontSize="11px">
                {new Date(r.created_at).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
              </Td>
              <Td fontSize="11px">{r.tenant_name || r.tenant_id}</Td>
              <Td fontSize="11px">{r.username || '—'}</Td>
              <Td>
                <Badge colorScheme={eventColor(r.event_type)} fontSize="9px">{r.event_type}</Badge>
              </Td>
              <Td fontSize="10px" color="gray.500">{r.ip_address ? r.ip_address.replace(/^::ffff:/i, '') : '—'}</Td>
              <Td fontSize="10px" color="gray.500" maxW="180px" isTruncated>{r.details || '—'}</Td>
            </Tr>
          ))}
          {rows.length === 0 && (
            <Tr><Td colSpan={6} textAlign="center" py={6} color="gray.400" fontSize="12px">No activity yet.</Td></Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}

// ── Tenant row ────────────────────────────────────────────────────────────────
function TenantRow({ tenant, api, onRefresh }) {
  const toast = useToast();
  const userDisc = useDisclosure();
  const featDisc = useDisclosure();
  const [features, setFeatures] = React.useState(null);
  const [savingFeatures, setSavingFeatures] = React.useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', display_name: '', email: '', password: '', role: 'user' });
  const [resettingId, setResettingId] = useState(null);
  const [tempPwd, setTempPwd] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(tenant.name);

  const handleRename = async () => {
    if (!newName.trim() || newName === tenant.name) { setRenaming(false); return; }
    try {
      await api.put(`/tenants/${tenant.id}`, { name: newName.trim() });
      toast({ title: 'Tenant renamed', status: 'success', duration: 1500 });
      setRenaming(false); onRefresh();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const FEATURE_DEFS = [
    { key: 'todo', label: 'To Do Lists', section: 'Actions' },
    { key: 'prospecting', label: 'Prospecting', section: 'Sales' },
    { key: 'pipeline', label: 'Business Pipeline', section: 'Sales' },
    { key: 'companies', label: 'Companies', section: 'Sales' },
    { key: 'contacts', label: 'Contacts', section: 'Sales' },
    { key: 'engagement', label: 'View Engagement', section: 'Sales' },
    { key: 'products', label: 'Products & Services', section: 'Products' },
    { key: 'supplier_products', label: 'Supplier Products', section: 'Products' },
    { key: 'resources', label: 'Resources', section: 'Products' },
    { key: 'nominal_codes', label: 'Nominal Codes', section: 'Products' },
    { key: 'departments', label: 'Departments', section: 'Admin' },
    { key: 'bookkeeper', label: 'Bookkeeper', section: 'Admin' },
    { key: 'bank_ledger', label: 'Bank Account Ledger', section: 'Admin' },
    { key: 'mileage', label: 'Mileage Reports', section: 'Admin' },
    { key: 'annual_leave', label: 'Annual Leave', section: 'Admin' },
    { key: 'timesheets', label: 'Timesheets', section: 'Admin' },
    { key: 'payroll', label: 'Payroll', section: 'Admin' },
    { key: 'site_visitors', label: 'Site Visitors', section: 'Admin' },
    { key: 'book_diary', label: 'Book My Diary', section: 'Sales' },
  ];
  const SECTIONS = ['Actions', 'Sales', 'Products', 'Admin'];

  const loadFeatures = async () => {
    try {
      const { data } = await api.get(`/tenants/${tenant.id}/features`);
      setFeatures(data);
    } catch(e) { toast({ title: 'Failed to load features', status: 'error', duration: 2000 }); }
  };

  const saveFeatures = async () => {
    setSavingFeatures(true);
    try {
      await api.put(`/tenants/${tenant.id}/features`, features);
      toast({ title: 'Features saved', status: 'success', duration: 1500 });
      featDisc.onClose();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSavingFeatures(false);
  };

  const openFeatures = () => { loadFeatures(); featDisc.onOpen(); };

  const toggleFeature = (key) => setFeatures(prev => ({ ...prev, [key]: !prev[key] }));

  const enableAll = () => setFeatures(prev => Object.fromEntries(Object.keys(prev).map(k => [k, true])));
  const disableAll = () => setFeatures(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));

  const loadUsers = async () => {
    setLoadingUsers(true);
    try { const { data } = await api.get(`/tenants/${tenant.id}/users`); setUsers(data); }
    catch(e) { toast({ title: 'Failed to load users', status: 'error', duration: 2000 }); }
    finally { setLoadingUsers(false); }
  };

  const openUsers = () => { userDisc.onOpen(); loadUsers(); };

  const toggleStatus = async () => {
    const newStatus = tenant.status === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/tenants/${tenant.id}`, { status: newStatus });
      toast({ title: `Tenant ${newStatus}`, status: 'success', duration: 1500 });
      onRefresh();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const toggleUserStatus = async (u) => {
    try {
      await api.put(`/users/${u.id}/status`, { is_active: !u.is_active });
      loadUsers();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const resetPassword = async (uid) => {
    setResettingId(uid); setTempPwd(null);
    try {
      const { data } = await api.post(`/users/${uid}/reset-password`, {});
      if (data.temp_password) setTempPwd(data.temp_password);
      toast({ title: 'Password reset', status: 'success', duration: 2000 });
      loadUsers();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setResettingId(null); }
  };

  const createUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast({ title: 'Username and password required', status: 'warning', duration: 2000 }); return;
    }
    try {
      await api.post(`/tenants/${tenant.id}/users`, newUser);
      toast({ title: 'User created', status: 'success', duration: 1500 });
      setNewUser({ username: '', display_name: '', email: '', password: '', role: 'user' });
      loadUsers();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  const statusColor = { active: 'green', paused: 'orange', suspended: 'red' }[tenant.status] || 'gray';

  return (
    <>
      <Tr _hover={{ bg: 'gray.50' }}>
        <Td fontWeight="600" fontSize="13px">
          {renaming ? (
            <HStack spacing={1}>
              <Input size="xs" value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
                autoFocus w="140px" />
              <IconButton size="xs" icon={<FiSave size={11}/>} colorScheme="green" variant="ghost" onClick={handleRename} aria-label="Save" />
              <IconButton size="xs" icon={<FiX size={11}/>} variant="ghost" onClick={() => { setRenaming(false); setNewName(tenant.name); }} aria-label="Cancel" />
            </HStack>
          ) : (
            <HStack spacing={1}>
              <Text>{tenant.name}</Text>
              <IconButton size="xs" icon={<FiEdit2 size={11}/>} variant="ghost" colorScheme="gray"
                onClick={() => { setNewName(tenant.name); setRenaming(true); }} aria-label="Rename" />
            </HStack>
          )}
        </Td>
        <Td fontSize="11px" color="gray.500"><Code fontSize="10px">{tenant.slug}</Code></Td>
        <Td><Badge colorScheme={statusColor} fontSize="9px">{tenant.status}</Badge></Td>
        <Td fontSize="11px">{tenant.plan}</Td>
        <Td fontSize="11px" isNumeric>{tenant.user_count ?? 0} / {tenant.max_users}</Td>
        <Td fontSize="11px" isNumeric>{tenant.project_count ?? 0}</Td>
        <Td fontSize="11px" color="gray.400">
          {tenant.last_login ? new Date(tenant.last_login).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : 'Never'}
        </Td>
        <Td>
          <HStack spacing={1}>
            <IconButton size="xs" icon={<FiUsers size={12} />} colorScheme="blue" variant="ghost"
              aria-label="Users" onClick={openUsers} />
            <IconButton size="xs" icon={<FiSettings size={12} />} colorScheme="purple" variant="ghost"
              aria-label="Features" title="Manage Power Dashboard features" onClick={openFeatures} />
            <IconButton size="xs"
              icon={tenant.status === 'active' ? <FiPause size={12} /> : <FiPlay size={12} />}
              colorScheme={tenant.status === 'active' ? 'orange' : 'green'} variant="ghost"
              aria-label="Toggle" onClick={toggleStatus} />
          </HStack>
        </Td>
      </Tr>

      {/* Users modal */}
      <Modal isOpen={userDisc.isOpen} onClose={userDisc.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            <HStack><FiUsers /><Text>Users — {tenant.name}</Text>
              <Badge colorScheme={statusColor}>{tenant.status}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* New user form */}
              <Box bg="blue.50" p={3} borderRadius="md">
                <Text fontSize="10px" fontWeight="700" color="blue.700" textTransform="uppercase" mb={2}>Add User</Text>
                <SimpleGrid columns={3} spacing={2} mb={2}>
                  <FormControl><FormLabel fontSize="xs" mb={0}>Username *</FormLabel>
                    <Input size="sm" value={newUser.username} onChange={e => setNewUser(p=>({...p,username:e.target.value}))} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>Display Name</FormLabel>
                    <Input size="sm" value={newUser.display_name} onChange={e => setNewUser(p=>({...p,display_name:e.target.value}))} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>Email</FormLabel>
                    <Input size="sm" type="email" value={newUser.email} onChange={e => setNewUser(p=>({...p,email:e.target.value}))} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>Password *</FormLabel>
                    <Input size="sm" type="password" value={newUser.password} onChange={e => setNewUser(p=>({...p,password:e.target.value}))} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>Role</FormLabel>
                    <Select size="sm" value={newUser.role} onChange={e => setNewUser(p=>({...p,role:e.target.value}))}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="restricted">Restricted</option>
                      <option value="accounts_manager">Accounts Manager</option>
                    </Select></FormControl>
                  <Flex align="flex-end">
                    <Button size="sm" colorScheme="blue" leftIcon={<FiPlus size={12}/>} onClick={createUser}>
                      Add User
                    </Button>
                  </Flex>
                </SimpleGrid>
              </Box>

              {/* Temp password display */}
              {tempPwd && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontSize="12px" fontWeight="700">Temporary password (show to user then dismiss):</Text>
                    <Code fontSize="14px" fontWeight="800">{tempPwd}</Code>
                  </Box>
                  <Button size="xs" ml="auto" onClick={() => setTempPwd(null)}>Dismiss</Button>
                </Alert>
              )}

              {/* Users table */}
              <Table size="sm">
                <Thead><Tr>
                  <Th>Username</Th><Th>Display Name</Th><Th>Email</Th><Th>Role</Th>
                  <Th>Status</Th><Th>Created</Th><Th></Th>
                </Tr></Thead>
                <Tbody>
                  {users.map(u => (
                    <Tr key={u.id} opacity={u.deleted_at ? 0.5 : 1}>
                      <Td fontSize="12px" fontWeight="600">{u.username}</Td>
                      <Td fontSize="12px">{u.display_name}</Td>
                      <Td fontSize="11px" color="gray.500">{u.email || '—'}</Td>
                      <Td><Badge fontSize="9px" colorScheme={u.role === 'admin' ? 'purple' : 'gray'}>{u.role}</Badge></Td>
                      <Td>
                        <HStack spacing={1}>
                          <Switch size="sm" isChecked={u.is_active !== false && !u.deleted_at}
                            onChange={() => toggleUserStatus(u)} isDisabled={!!u.deleted_at} />
                          <Text fontSize="10px" color={u.is_active !== false ? 'green.500' : 'orange.400'}>
                            {u.deleted_at ? 'Deleted' : u.is_active !== false ? 'Active' : 'Paused'}
                          </Text>
                        </HStack>
                      </Td>
                      <Td fontSize="10px" color="gray.400">
                        {new Date(u.created_at).toLocaleDateString('en-GB')}
                      </Td>
                      <Td>
                        <IconButton size="xs" icon={<FiKey size={11}/>} colorScheme="orange" variant="ghost"
                          aria-label="Reset password" isLoading={resettingId === u.id}
                          onClick={() => resetPassword(u.id)} />
                      </Td>
                    </Tr>
                  ))}
                  {users.length === 0 && !loadingUsers && (
                    <Tr><Td colSpan={7} textAlign="center" color="gray.400" fontSize="12px" py={4}>No users yet.</Td></Tr>
                  )}
                </Tbody>
              </Table>
            </VStack>
          </ModalBody>
          <ModalFooter><Button size="sm" onClick={userDisc.onClose}>Close</Button></ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ── New Tenant modal ──────────────────────────────────────────────────────────
function NewTenantModal({ isOpen, onClose, api, onCreated }) {
  const [form, setForm] = useState({ name: '', slug: '', plan: 'standard', max_users: 50 });
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const autoSlug = (name) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleCreate = async () => {
    if (!form.name || !form.slug) { toast({ title: 'Name and slug required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/tenants', form);
      toast({ title: `Tenant "${data.name}" created`, status: 'success', duration: 2000 });
      onCreated(); onClose();
      setForm({ name: '', slug: '', plan: 'standard', max_users: 50 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">New Tenant</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs">Tenant Name *</FormLabel>
              <Input value={form.name} onChange={e => { set('name', e.target.value); if (!form.slug) set('slug', autoSlug(e.target.value)); }} placeholder="Acme Productions" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Slug * <Text as="span" color="gray.400">(unique identifier, URL-safe)</Text></FormLabel>
              <Input value={form.slug} onChange={e => set('slug', autoSlug(e.target.value))} placeholder="acme-productions" />
            </FormControl>
            <HStack w="100%" spacing={3}>
              <FormControl flex={1}>
                <FormLabel fontSize="xs">Plan</FormLabel>
                <Select value={form.plan} onChange={e => set('plan', e.target.value)}>
                  <option value="standard">Standard</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </Select>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="xs">Max Users</FormLabel>
                <Input type="number" value={form.max_users} onChange={e => set('max_users', parseInt(e.target.value))} />
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" isLoading={saving} onClick={handleCreate}>Create Tenant</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── MFA Settings Panel ────────────────────────────────────────────────────────
function MfaSettingsPanel({ api, admin }) {
  const [status, setStatus] = useState(null);   // null | { totp_enabled }
  const [phase, setPhase] = useState('idle');    // idle | setup | verify | disable
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get('/mfa/status'); setStatus(data); } catch(e) {}
  }, [api]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/mfa/setup');
      setSecret(data.secret);
      setQrDataUrl(data.qrDataUrl || '');
      setCode('');
      setPhase('setup');
    } catch(e) {
      toast({ title: 'Failed to generate QR code', status: 'error', duration: 2000 });
    } finally { setLoading(false); }
  };

  const verifyCode = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await api.post('/mfa/verify', { code });
      toast({ title: '✓ Authenticator enabled', status: 'success', duration: 2000 });
      setPhase('idle'); setCode(''); setQrDataUrl(''); setSecret('');
      loadStatus();
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Invalid code — try again', status: 'error', duration: 2000 });
      setCode('');
    } finally { setLoading(false); }
  };

  const disableMfa = async () => {
    if (disableCode.length !== 6) return;
    setLoading(true);
    try {
      await api.delete('/mfa', { code: disableCode });
      toast({ title: 'Authenticator disabled', status: 'info', duration: 2000 });
      setPhase('idle'); setDisableCode('');
      loadStatus();
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Invalid code', status: 'error', duration: 2000 });
      setDisableCode('');
    } finally { setLoading(false); }
  };

  const qrImageUrl = qrDataUrl; // served from backend as base64 data URL

  return (
    <Box maxW="520px">
      <Text fontSize="15px" fontWeight="700" mb={1}>Two-Factor Authentication</Text>
      <Text fontSize="12px" color="gray.500" mb={6}>
        Protect your super-admin account with Google Authenticator.
        Once enabled, a 6-digit code is required at every login.
      </Text>

      {/* Current status badge */}
      {status !== null && (
        <HStack mb={6} p={3} bg={status.totp_enabled ? 'green.50' : 'orange.50'}
          borderRadius="lg" border="1px" borderColor={status.totp_enabled ? 'green.200' : 'orange.200'}>
          {status.totp_enabled
            ? <><FiCheckCircle size={18} color="green" /><Text fontSize="13px" fontWeight="600" color="green.700">2FA is enabled</Text></>
            : <><FiSmartphone size={18} color="orange" /><Text fontSize="13px" fontWeight="600" color="orange.700">2FA is not enabled</Text></>}
        </HStack>
      )}

      {/* ── Idle: show enable / disable button ── */}
      {phase === 'idle' && (
        <>
          {!status?.totp_enabled ? (
            <Button colorScheme="blue" leftIcon={<FiSmartphone size={15}/>}
              onClick={startSetup} isLoading={loading}>
              Set up Google Authenticator
            </Button>
          ) : (
            <Button colorScheme="red" variant="outline" leftIcon={<FiLock size={15}/>}
              onClick={() => { setPhase('disable'); setDisableCode(''); }}>
              Disable Authenticator
            </Button>
          )}
        </>
      )}

      {/* ── Setup: show QR code ── */}
      {phase === 'setup' && (
        <VStack spacing={4} align="stretch">
          <Box bg="blue.50" p={4} borderRadius="lg" border="1px" borderColor="blue.200">
            <Text fontSize="12px" fontWeight="700" color="blue.700" mb={3}>
              Step 1 — Scan this QR code with Google Authenticator
            </Text>
            <Flex justify="center" mb={3}>
              <Box bg="white" p={2} borderRadius="md" boxShadow="sm">
                <img src={qrImageUrl} alt="QR Code" width={220} height={220}
                  style={{ display: 'block' }} />
              </Box>
            </Flex>
            <Text fontSize="10px" color="blue.600" textAlign="center" mb={2}>
              Can't scan? Enter this code manually in the app:
            </Text>
            <Code display="block" textAlign="center" fontSize="13px" fontWeight="800"
              letterSpacing="0.15em" bg="blue.100" p={2} borderRadius="md">
              {secret}
            </Code>
          </Box>

          <Box bg="gray.50" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
            <Text fontSize="12px" fontWeight="700" color="gray.700" mb={3}>
              Step 2 — Enter the 6-digit code from the app to confirm
            </Text>
            <FormControl mb={3}>
              <Input fontSize="24px" textAlign="center" letterSpacing="0.3em"
                maxLength={6} value={code} autoFocus
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" />
            </FormControl>
            <HStack>
              <Button variant="ghost" onClick={() => { setPhase('idle'); setCode(''); setQrDataUrl(''); setSecret(''); }}>
                Cancel
              </Button>
              <Button colorScheme="green" leftIcon={<FiCheckCircle size={14}/>}
                isDisabled={code.length !== 6} isLoading={loading} onClick={verifyCode}>
                Verify &amp; Enable
              </Button>
            </HStack>
          </Box>
        </VStack>
      )}

      {/* ── Disable: confirm with current code ── */}
      {phase === 'disable' && (
        <Box bg="red.50" p={4} borderRadius="lg" border="1px" borderColor="red.200">
          <Text fontSize="12px" fontWeight="700" color="red.700" mb={1}>
            Confirm with your current authenticator code
          </Text>
          <Text fontSize="11px" color="red.500" mb={3}>
            Enter a valid 6-digit code to disable 2FA. You will not be prompted for a code on your next login.
          </Text>
          <FormControl mb={3}>
            <Input bg="white" fontSize="22px" textAlign="center" letterSpacing="0.3em"
              maxLength={6} value={disableCode} autoFocus
              onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" />
          </FormControl>
          <HStack>
            <Button variant="ghost" onClick={() => { setPhase('idle'); setDisableCode(''); }}>
              Cancel
            </Button>
            <Button colorScheme="red" isDisabled={disableCode.length !== 6}
              isLoading={loading} onClick={disableMfa}>
              Disable 2FA
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
}

// ── System Email Panel ────────────────────────────────────────────────────────
function SystemEmailPanel({ api }) {
  const [configs, setConfigs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    label: 'Default', host: 'mail.forgebos.com', port: 587, secure: false,
    auth_user: '', auth_pass: '', from_name: 'ForgeBOS', from_email: '',
    imap_host: 'mail.forgebos.com', imap_port: 993,
    pop_host: 'mail.forgebos.com', pop_port: 995,
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testLog, setTestLog] = useState('');
  const [editMode, setEditMode] = useState(false);
  const toast = useToast();
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadConfigs = useCallback(async () => {
    try { const { data } = await api.get('/system-email'); setConfigs(data); if (data.length && !selected) setSelected(data[0]); }
    catch(e) {}
  }, [api]); // eslint-disable-line

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editMode && selected) {
        const { data } = await api.put(`/system-email/${selected.id}`, form);
        setSelected(data); setEditMode(false);
        toast({ title: 'Saved', status: 'success', duration: 1500 });
      } else {
        const { data } = await api.post('/system-email', form);
        setSelected(data); setEditMode(false);
        toast({ title: 'Created', status: 'success', duration: 1500 });
      }
      await loadConfigs();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!selected) return;
    setTesting(true); setTestLog('Connecting...\n');
    try {
      const response = await fetch(
        `/api/t-admin/system-email/${selected.id}/test`,
        { method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('tadmin_token')}` },
          body: JSON.stringify({ to_email: testTo || undefined }) }
      );
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          const text = decoder.decode(value);
          const lines = text.split('\n').filter(l => l.startsWith('data:'));
          lines.forEach(l => {
            try { const msg = JSON.parse(l.slice(5)); setTestLog(p => p + msg + '\n'); }
            catch(e) {}
          });
        }
      }
    } catch(e) { setTestLog(p => p + `\nError: ${e.message}`); }
    finally { setTesting(false); }
  };

  const startEdit = () => {
    if (!selected) return;
    setForm({ ...selected, auth_pass: '' });
    setEditMode(true);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="15px" fontWeight="700">System Email Configuration</Text>
        <Button size="sm" colorScheme="blue" leftIcon={<FiPlus size={13}/>}
          onClick={() => { setSelected(null); setForm({ label:'Default', host:'mail.forgebos.com', port:587, secure:false, auth_user:'', auth_pass:'', from_name:'ForgeBOS', from_email:'', imap_host:'mail.forgebos.com', imap_port:993, pop_host:'mail.forgebos.com', pop_port:995 }); setEditMode(true); }}>
          Add Config
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {/* Config list */}
        <Box>
          {configs.length === 0 ? (
            <Box bg="orange.50" p={4} borderRadius="lg" border="1px" borderColor="orange.200">
              <Text fontSize="13px" color="orange.700" fontWeight="600">No email config yet</Text>
              <Text fontSize="12px" color="orange.600" mt={1}>Add a config to enable password reset emails and system notifications.</Text>
            </Box>
          ) : (
            <VStack spacing={2} align="stretch">
              {configs.map(c => (
                <Box key={c.id} p={3} borderRadius="md" border="2px"
                  borderColor={selected?.id === c.id ? 'blue.400' : 'gray.200'}
                  bg={selected?.id === c.id ? 'blue.50' : 'white'}
                  cursor="pointer" onClick={() => { setSelected(c); setEditMode(false); }}>
                  <HStack justify="space-between">
                    <Box>
                      <Text fontSize="13px" fontWeight="600">{c.label}</Text>
                      <Text fontSize="11px" color="gray.500">{c.from_email} · {c.host}:{c.port}</Text>
                    </Box>
                    <HStack>
                      <Badge colorScheme={c.is_active ? 'green' : 'gray'} fontSize="8px">
                        {c.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                      <IconButton size="xs" icon={<FiEdit2 size={11}/>} variant="ghost"
                        onClick={e => { e.stopPropagation(); setSelected(c); startEdit(); }} aria-label="Edit" />
                    </HStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </Box>

        {/* Form / Detail */}
        <Box>
          {editMode ? (
            <Box bg="gray.50" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
              <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase" mb={3}>
                {selected ? 'Edit Config' : 'New Config'}
              </Text>
              <VStack spacing={2}>
                <SimpleGrid columns={2} spacing={2} w="100%">
                  <FormControl><FormLabel fontSize="xs" mb={0}>Label</FormLabel>
                    <Input size="sm" value={form.label} onChange={e => set('label', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>From Name</FormLabel>
                    <Input size="sm" value={form.from_name} onChange={e => set('from_name', e.target.value)} /></FormControl>
                </SimpleGrid>
                <FormControl><FormLabel fontSize="xs" mb={0}>From Email Address *</FormLabel>
                  <Input size="sm" type="email" value={form.from_email} onChange={e => set('from_email', e.target.value)} placeholder="noreply@forgebos.com" /></FormControl>

                <Divider /><Text fontSize="10px" fontWeight="700" color="blue.600" textTransform="uppercase" w="100%">SMTP (Outgoing)</Text>
                <SimpleGrid columns={2} spacing={2} w="100%">
                  <FormControl><FormLabel fontSize="xs" mb={0}>SMTP Host *</FormLabel>
                    <Input size="sm" value={form.host} onChange={e => set('host', e.target.value)} placeholder="mail.forgebos.com" /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>Port</FormLabel>
                    <Input size="sm" type="number" value={form.port} onChange={e => set('port', parseInt(e.target.value))} /></FormControl>
                </SimpleGrid>
                <FormControl><FormLabel fontSize="xs" mb={0}>Username (email address) *</FormLabel>
                  <Input size="sm" value={form.auth_user} onChange={e => set('auth_user', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="xs" mb={0}>Password {selected ? '(leave blank to keep current)' : '*'}</FormLabel>
                  <InputGroup size="sm">
                    <Input type={showPass ? 'text' : 'password'} value={form.auth_pass}
                      onChange={e => set('auth_pass', e.target.value)} />
                    <InputRightElement>
                      <Button size="xs" variant="ghost" onClick={() => setShowPass(p=>!p)}>
                        {showPass ? 'Hide' : 'Show'}
                      </Button>
                    </InputRightElement>
                  </InputGroup></FormControl>
                <HStack w="100%">
                  <Switch isChecked={form.secure} onChange={e => set('secure', e.target.checked)} size="sm" />
                  <Text fontSize="12px">Use SSL/TLS (port 465)</Text>
                </HStack>
                <HStack w="100%" bg="orange.50" p={2} borderRadius="md" border="1px" borderColor="orange.200">
                  <Switch isChecked={!!form.allow_self_signed} onChange={e => set('allow_self_signed', e.target.checked)} size="sm" colorScheme="orange" />
                  <Box>
                    <Text fontSize="12px" fontWeight="600" color="orange.700">Allow expired / self-signed certificates</Text>
                    <Text fontSize="10px" color="orange.600">Disables TLS certificate verification. Use if your mail server has an expired or self-signed cert.</Text>
                  </Box>
                </HStack>

                <Divider /><Text fontSize="10px" fontWeight="700" color="purple.600" textTransform="uppercase" w="100%">IMAP (Incoming — optional)</Text>
                <SimpleGrid columns={2} spacing={2} w="100%">
                  <FormControl><FormLabel fontSize="xs" mb={0}>IMAP Host</FormLabel>
                    <Input size="sm" value={form.imap_host} onChange={e => set('imap_host', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>IMAP Port</FormLabel>
                    <Input size="sm" type="number" value={form.imap_port} onChange={e => set('imap_port', parseInt(e.target.value))} /></FormControl>
                </SimpleGrid>

                <Divider /><Text fontSize="10px" fontWeight="700" color="teal.600" textTransform="uppercase" w="100%">POP3 (Incoming — optional)</Text>
                <SimpleGrid columns={2} spacing={2} w="100%">
                  <FormControl><FormLabel fontSize="xs" mb={0}>POP3 Host</FormLabel>
                    <Input size="sm" value={form.pop_host} onChange={e => set('pop_host', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0}>POP3 Port</FormLabel>
                    <Input size="sm" type="number" value={form.pop_port} onChange={e => set('pop_port', parseInt(e.target.value))} /></FormControl>
                </SimpleGrid>

                <HStack w="100%" pt={1}>
                  <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button size="sm" colorScheme="blue" isLoading={saving} leftIcon={<FiSave size={13}/>} onClick={handleSave}>
                    {selected ? 'Save Changes' : 'Create Config'}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          ) : selected ? (
            <Box bg="white" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
              <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={3}>Test Connection</Text>
              <FormControl mb={2}>
                <FormLabel fontSize="xs" mb={0}>Send test email to (leave blank = auth_user)</FormLabel>
                <Input size="sm" type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                  placeholder={selected.auth_user} />
              </FormControl>
              <Button size="sm" colorScheme="green" leftIcon={testing ? <Spinner size="xs"/> : <FiSend size={13}/>}
                onClick={handleTest} isDisabled={testing} mb={3}>
                {testing ? 'Sending...' : 'Send Test Email'}
              </Button>
              {testLog && (
                <Box>
                  <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
                    SMTP Interaction Log
                  </Text>
                  <Textarea
                    value={testLog} readOnly rows={12} fontSize="11px" fontFamily="mono"
                    bg="gray.900" color="green.300" border="none" resize="vertical"
                    _readOnly={{ cursor: 'text' }} />
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      </SimpleGrid>
    </Box>
  );
}

// ── Debug Panel ───────────────────────────────────────────────────────────────
const LEVEL_COLORS = { error: '#fc8181', warn: '#f6e05e', info: '#68d391' };

function DebugPanel({ token }) {
  const [lines, setLines] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [filter, setFilter] = useState('all');
  const [paused, setPaused] = useState(false);
  const [search, setSearch] = useState('');
  const [rebuilding, setRebuilding] = useState(false);
  const [rebooting, setRebooting] = useState(false);
  const [showFormLogs, setShowFormLogs] = useState(false);
  const [fullBackupLoading, setFullBackupLoading] = useState(false);
  const logRef = useRef(null);
  const esRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const appendLines = (newEntries) => {
    setLines(prev => [...prev, ...newEntries].slice(-1000));
  };

  // Stream from a POST endpoint (rebuild / reboot)
  const streamPost = async (path, onStart, onDone) => {
    onStart();
    try {
      const response = await fetch(`/api/t-admin${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (part.startsWith('data: ')) {
            try {
              const entry = JSON.parse(part.slice(6));
              setLines(prev => [...prev, entry].slice(-1000));
            } catch(e) {}
          }
        }
      }
    } catch(e) {
      appendLines([{ ts: new Date().toISOString(), level: 'error', msg: `Stream error: ${e.message}` }]);
    } finally { onDone(); }
  };

  const handleRebuild = () => {
    if (!window.confirm('Rebuild the system? This will take 2-5 minutes and cause a brief outage while containers restart.')) return;
    appendLines([{ ts: new Date().toISOString(), level: 'warn', msg: '══════════ REBUILD STARTED ══════════' }]);
    streamPost('/debug/rebuild', () => setRebuilding(true), () => setRebuilding(false));
  };

  const handleReboot = () => {
    if (!window.confirm('⚠ REBOOT SERVER?\n\nThe entire server will restart. The app will be offline for ~60 seconds. Are you sure?')) return;
    if (!window.confirm('Are you absolutely sure? This will disconnect all active users.')) return;
    appendLines([{ ts: new Date().toISOString(), level: 'warn', msg: '══════════ SERVER REBOOT INITIATED ══════════' }]);
    streamPost('/debug/reboot', () => setRebooting(true), () => setRebooting(false));
  };

  const startStream = () => {
    if (esRef.current) esRef.current.close();
    const es = new EventSource(
      `/api/t-admin/debug/stream`,
      // EventSource doesn't support headers — pass token as query param
      // We'll use a short-lived approach via fetch instead
    );
    // EventSource can't send auth headers — use fetch with ReadableStream
    esRef.current = null;
    setStreaming(true);

    fetch('/api/t-admin/debug/stream', {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async response => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (part.startsWith('data: ')) {
            try {
              const entry = JSON.parse(part.slice(6));
              if (!pausedRef.current) {
                setLines(prev => {
                  const next = [...prev, entry];
                  return next.slice(-1000); // keep last 1000
                });
              }
            } catch(e) {}
          }
        }
      }
    }).catch(e => {
      console.log('Debug stream ended:', e.message);
    }).finally(() => setStreaming(false));
  };

  const stopStream = () => {
    setStreaming(false);
  };

  // Auto-scroll to bottom unless paused
  useEffect(() => {
    if (!paused && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [lines, paused]);

  // Load recent logs on mount
  useEffect(() => {
    fetch('/api/t-admin/debug/logs', {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(data => {
      if (Array.isArray(data)) setLines(data);
    }).catch(() => {});
  }, [token]);

  const clearLogs = () => setLines([]);

  const handleFullBackup = async () => {
    setFullBackupLoading(true);
    appendLines([{ ts: new Date().toISOString(), level: 'info', msg: '[BACKUP] Starting full database export...' }]);
    try {
      const response = await fetch('/api/backup/full-export', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `forgebos_FULL_backup_${ts}.sql`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      appendLines([{ ts: new Date().toISOString(), level: 'info', msg: '[BACKUP] ✅ Full database export downloaded successfully.' }]);
    } catch(e) {
      appendLines([{ ts: new Date().toISOString(), level: 'error', msg: `[BACKUP] ❌ Full export failed: ${e.message}` }]);
    }
    setFullBackupLoading(false);
  };

  const filtered = lines.filter(l => {
    if (filter !== 'all' && l.level !== filter) return false;
    if (search && !l.msg.toLowerCase().includes(search.toLowerCase())) return false;
    // When showFormLogs is OFF, hide [WEB_FORM] and [KIOSK] log entries
    if (!showFormLogs && (l.msg.includes('[WEB_FORM]') || l.msg.includes('[KIOSK]'))) return false;
    return true;
  });

  const errorCount = lines.filter(l => l.level === 'error').length;
  const warnCount  = lines.filter(l => l.level === 'warn').length;

  return (
    <Box>
      {/* Action buttons row */}
      <HStack spacing={2} mb={3} p={3} bg="gray.800" borderRadius="md">
        <Text fontSize="11px" color="gray.400" mr={1}>System Actions:</Text>
        <Button size="sm" colorScheme="orange" leftIcon={<FiRefreshCw size={13}/>}
          isLoading={rebuilding} loadingText="Rebuilding..."
          onClick={handleRebuild}>
          Rebuild System
        </Button>
        <Button size="sm" colorScheme="red" variant="outline" borderColor="red.500"
          color="red.300" leftIcon={<FiAlertTriangle size={13}/>}
          isLoading={rebooting} loadingText="Rebooting..."
          onClick={handleReboot}>
          Reboot Server
        </Button>
        <Button size="sm"
          bg="rgba(255,255,255,0.08)"
          color="rgba(255,255,255,0.75)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.3)"
          _hover={{ bg: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.6)', color: 'white' }}
          leftIcon={<FiDownload size={13}/>}
          isLoading={fullBackupLoading}
          loadingText="Exporting…"
          onClick={handleFullBackup}>
          Full DB Backup
        </Button>
        <Text fontSize="10px" color="gray.500" ml={2}>
          Rebuild: docker compose up -d --build (2-5 min outage) · Reboot: full server restart (~60s)
        </Text>
        <Button size="sm"
          bg={showFormLogs ? 'teal.400' : 'rgba(255,255,255,0.08)'}
          color={showFormLogs ? 'white' : 'rgba(255,255,255,0.75)'}
          border="1px solid"
          borderColor={showFormLogs ? 'teal.300' : 'rgba(255,255,255,0.3)'}
          _hover={{ bg: showFormLogs ? 'teal.500' : 'rgba(255,255,255,0.18)', borderColor: showFormLogs ? 'teal.200' : 'rgba(255,255,255,0.6)', color: 'white' }}
          ml="auto"
          onClick={() => setShowFormLogs(p => !p)}>
          🌐 {showFormLogs ? '✓ Showing' : 'Show'} Kiosk / Form data
        </Button>
      </HStack>

      <Flex justify="space-between" align="center" mb={3}>
        <HStack spacing={3}>
          <Text fontSize="15px" fontWeight="700">Server Debug Log</Text>
          {errorCount > 0 && <Badge colorScheme="red">{errorCount} error{errorCount !== 1 ? 's' : ''}</Badge>}
          {warnCount > 0  && <Badge colorScheme="yellow">{warnCount} warning{warnCount !== 1 ? 's' : ''}</Badge>}
          {streaming && <Badge colorScheme="green" variant="outline">● LIVE</Badge>}
          {rebuilding && <Badge colorScheme="orange" variant="outline">⚙ REBUILDING</Badge>}
          {rebooting && <Badge colorScheme="red" variant="outline">⚠ REBOOTING</Badge>}
        </HStack>
        <HStack spacing={2}>
          <Input size="xs" placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)} w="160px" bg="white" />
          <Select size="xs" value={filter} onChange={e => setFilter(e.target.value)} w="90px" bg="white">
            <option value="all">All</option>
            <option value="error">Errors</option>
            <option value="warn">Warnings</option>
            <option value="info">Info</option>
          </Select>
          <Button size="xs" variant="outline" onClick={() => setPaused(p => !p)}
            colorScheme={paused ? 'orange' : 'gray'}>
            {paused ? 'Resume' : 'Pause'}
          </Button>
          <Button size="xs" variant="outline" colorScheme="red" onClick={clearLogs}>Clear</Button>
          {streaming
            ? <Button size="xs" colorScheme="red" onClick={stopStream}>Stop</Button>
            : <Button size="xs" colorScheme="green" leftIcon={<FiTerminal size={11}/>} onClick={startStream}>
                Live Stream
              </Button>
          }
        </HStack>
      </Flex>

      <Box
        ref={logRef}
        bg="gray.900" borderRadius="md" p={3}
        fontFamily="'Courier New', monospace" fontSize="11px"
        h="calc(100vh - 240px)" overflowY="auto"
        border="1px" borderColor="gray.700"
      >
        {filtered.length === 0 ? (
          <Text color="gray.500" textAlign="center" mt={8}>
            {streaming ? 'Waiting for log output...' : 'No logs yet — click "Live Stream" to connect'}
          </Text>
        ) : (
          filtered.map((l, i) => (
            <Flex key={i} mb="1px" align="flex-start" _hover={{ bg: 'gray.800' }}>
              <Text color="gray.500" mr={2} flexShrink={0} fontSize="10px" mt="1px">
                {new Date(l.ts).toLocaleTimeString()}
              </Text>
              <Text
                color={LEVEL_COLORS[l.level] || '#e2e8f0'}
                fontWeight={l.level === 'error' ? '700' : 'normal'}
                wordBreak="break-word"
                flex={1}
              >
                {l.level === 'error' && '✗ '}
                {l.level === 'warn'  && '⚠ '}
                {l.msg}
              </Text>
            </Flex>
          ))
        )}
      </Box>
      <Text fontSize="10px" color="gray.400" mt={1}>
        Showing {filtered.length} of {lines.length} lines (last 1000 kept in memory)
        {paused && <Text as="span" color="orange.400" ml={2}>— PAUSED</Text>}
      </Text>
    </Box>
  );
}

// ── Row Group Templates Panel ─────────────────────────────────────────────────
function RowGroupTemplatesPanel({ api, tenants }) {
  const [templates, setTemplates] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [applying, setApplying] = useState(null); // template id being applied
  const [applyTenantId, setApplyTenantId] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTmpl, setNewTmpl] = useState({ name:'', description:'', industry:'', icon:'FiGrid', groups:[] });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const loadTemplates = useCallback(async () => {
    try { const { data } = await api.get('/templates'); setTemplates(data); } catch(e) {}
  }, [api]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleApply = async (templateId) => {
    if (!applyTenantId) { toast({ title: 'Select a tenant first', status: 'warning', duration: 2000 }); return; }
    setApplying(templateId);
    try {
      const { data } = await api.post(`/tenants/${applyTenantId}/apply-template`,
        { template_id: templateId, replace_existing: replaceExisting });
      toast({ title: data.message, status: 'success', duration: 3000 });
      setApplying(null);
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
      setApplying(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this custom template?')) return;
    try {
      await api.delete(`/templates/${id}`);
      toast({ title: 'Deleted', status: 'info', duration: 1500 });
      loadTemplates();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleCreate = async () => {
    if (!newTmpl.name.trim()) { toast({ title: 'Template name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post('/templates', newTmpl);
      toast({ title: 'Template created', status: 'success', duration: 1500 });
      setShowCreate(false);
      setNewTmpl({ name:'', description:'', industry:'', icon:'FiGrid', groups:[] });
      loadTemplates();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const addGroup = () => setNewTmpl(p => ({ ...p, groups: [...p.groups, { name:'', sort_order: (p.groups.length+1)*10, color:'#2d3748' }] }));
  const setGroup = (i, k, v) => setNewTmpl(p => { const g=[...p.groups]; g[i]={...g[i],[k]:v}; return {...p,groups:g}; });
  const removeGroup = (i) => setNewTmpl(p => ({ ...p, groups: p.groups.filter((_,j)=>j!==i) }));

  const industryColors = {
    'Media & Entertainment': 'purple', 'Agency': 'blue', 'Trades': 'orange',
    'Professional Services': 'teal', 'Marketing': 'pink', 'Events': 'cyan',
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Box>
          <Text fontSize="15px" fontWeight="700">Row Group Templates</Text>
          <Text fontSize="12px" color="gray.500" mt={0.5}>
            Industry-specific project workflow templates. Apply to a tenant to add those row groups to their board.
          </Text>
        </Box>
        <Button size="sm" colorScheme="blue" leftIcon={<FiPlus size={13}/>} onClick={() => setShowCreate(p=>!p)}>
          {showCreate ? 'Cancel' : 'New Template'}
        </Button>
      </Flex>

      {/* Apply target selector — shown at the top for quick access */}
      <Box bg="blue.50" p={3} borderRadius="lg" border="1px" borderColor="blue.200" mb={4}>
        <Text fontSize="11px" fontWeight="700" color="blue.700" textTransform="uppercase" mb={2}>
          Apply template to tenant
        </Text>
        <HStack spacing={3} flexWrap="wrap">
          <FormControl w="220px">
            <Select size="sm" placeholder="Select tenant..." value={applyTenantId}
              onChange={e => setApplyTenantId(e.target.value)}>
              {(tenants||[]).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </FormControl>
          <HStack>
            <Switch size="sm" isChecked={replaceExisting} onChange={e => setReplaceExisting(e.target.checked)} />
            <Text fontSize="12px">Replace existing non-system groups</Text>
          </HStack>
          {!applyTenantId && <Text fontSize="11px" color="gray.400">← Select a tenant, then click Apply on any template below</Text>}
        </HStack>
      </Box>

      {/* Create new template form */}
      {showCreate && (
        <Box bg="gray.50" p={4} borderRadius="lg" border="1px" borderColor="gray.300" mb={4}>
          <Text fontSize="12px" fontWeight="700" color="gray.700" textTransform="uppercase" mb={3}>New Custom Template</Text>
          <SimpleGrid columns={{ base:1, md:3 }} spacing={3} mb={3}>
            <FormControl><FormLabel fontSize="xs" mb={0}>Template Name *</FormLabel>
              <Input size="sm" value={newTmpl.name} onChange={e => setNewTmpl(p=>({...p,name:e.target.value}))} /></FormControl>
            <FormControl><FormLabel fontSize="xs" mb={0}>Industry</FormLabel>
              <Input size="sm" value={newTmpl.industry} onChange={e => setNewTmpl(p=>({...p,industry:e.target.value}))} placeholder="e.g. Healthcare" /></FormControl>
            <FormControl><FormLabel fontSize="xs" mb={0}>Description</FormLabel>
              <Input size="sm" value={newTmpl.description} onChange={e => setNewTmpl(p=>({...p,description:e.target.value}))} /></FormControl>
          </SimpleGrid>

          <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>Row Groups</Text>
          <VStack spacing={2} align="stretch" mb={3}>
            {newTmpl.groups.map((g, i) => (
              <HStack key={i} spacing={2}>
                <Input size="xs" flex={3} placeholder="Group name" value={g.name} onChange={e => setGroup(i,'name',e.target.value)} />
                <Input size="xs" w="70px" type="number" placeholder="Order" value={g.sort_order} onChange={e => setGroup(i,'sort_order',parseInt(e.target.value))} />
                <HStack spacing={1}>
                  <Box w="20px" h="20px" borderRadius="sm" bg={g.color} border="1px" borderColor="gray.300" cursor="pointer" />
                  <Input size="xs" w="80px" value={g.color} onChange={e => setGroup(i,'color',e.target.value)} />
                </HStack>
                <IconButton size="xs" icon={<FiX size={11}/>} variant="ghost" colorScheme="red" onClick={() => removeGroup(i)} aria-label="Remove" />
              </HStack>
            ))}
            <Button size="xs" variant="outline" leftIcon={<FiPlus size={11}/>} onClick={addGroup} alignSelf="flex-start">
              Add Group
            </Button>
          </VStack>
          <HStack>
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" colorScheme="blue" leftIcon={<FiSave size={13}/>} isLoading={saving} onClick={handleCreate}>
              Save Template
            </Button>
          </HStack>
        </Box>
      )}

      {/* Template cards */}
      <SimpleGrid columns={{ base:1, md:2, lg:3 }} spacing={4}>
        {templates.map(t => {
          const color = industryColors[t.industry] || 'gray';
          const isExpanded = expanded === t.id;
          return (
            <Box key={t.id} bg="white" borderRadius="lg" border="2px" borderColor={isExpanded ? `${color}.300` : 'gray.200'}
              boxShadow="sm" overflow="hidden">
              <Box p={3}>
                <Flex justify="space-between" align="flex-start">
                  <Box flex={1}>
                    <HStack spacing={2} mb={1}>
                      {t.industry && <Badge colorScheme={color} fontSize="8px">{t.industry}</Badge>}
                      {t.is_built_in ? <Badge colorScheme="gray" fontSize="8px">Built-in</Badge>
                        : <Badge colorScheme="blue" fontSize="8px">Custom</Badge>}
                    </HStack>
                    <Text fontSize="13px" fontWeight="700">{t.name}</Text>
                    {t.description && <Text fontSize="11px" color="gray.500" mt={0.5} noOfLines={2}>{t.description}</Text>}
                  </Box>
                  <HStack spacing={1} ml={2}>
                    {!t.is_built_in && (
                      <IconButton size="xs" icon={<FiTrash2 size={11}/>} variant="ghost" colorScheme="red"
                        onClick={() => handleDelete(t.id)} aria-label="Delete" />
                    )}
                    <IconButton size="xs" variant="ghost"
                      icon={isExpanded ? <FiChevronUp size={13}/> : <FiChevronDown size={13}/>}
                      onClick={() => setExpanded(isExpanded ? null : t.id)} aria-label="Expand" />
                  </HStack>
                </Flex>

                {/* Group count */}
                <Text fontSize="10px" color="gray.400" mt={1}>
                  {(t.groups||[]).length} row group{(t.groups||[]).length !== 1 ? 's' : ''}
                </Text>
              </Box>

              {/* Expanded: show groups */}
              {isExpanded && (
                <Box borderTop="1px" borderColor="gray.100" bg="gray.50" p={3}>
                  <VStack spacing={1} align="stretch" mb={3}>
                    {(t.groups||[]).map(g => (
                      <HStack key={g.id} spacing={2}>
                        <Box w="10px" h="10px" borderRadius="sm" bg={g.color} flexShrink={0} />
                        <Text fontSize="11px">{g.name}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <Button size="xs" colorScheme={color} w="100%"
                    isLoading={applying === t.id}
                    isDisabled={!applyTenantId}
                    onClick={() => handleApply(t.id)}>
                    {applyTenantId
                      ? `Apply to ${tenants?.find(ten=>String(ten.id)===String(applyTenantId))?.name || 'tenant'}`
                      : 'Select a tenant above to apply'}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

// ── Security Settings Panel ────────────────────────────────────────────────────
function SecuritySettingsPanel({ api, tenants }) {
  const [tenantId, setTenantId] = useState(1);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const loadSettings = useCallback(async () => {
    try { const { data } = await api.get(`/system-email/security-settings?tenant_id=${tenantId}`); setSettings(data); }
    catch(e) {}
  }, [api, tenantId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/system-email/security-settings', { ...settings, tenant_id: tenantId });
      toast({ title: 'Security settings saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  return (
    <Box maxW="560px">
      <Text fontSize="15px" fontWeight="700" mb={1}>Security Settings</Text>
      <Text fontSize="12px" color="gray.500" mb={4}>Configure per-tenant security policy.</Text>

      <FormControl mb={4}>
        <FormLabel fontSize="xs">Tenant</FormLabel>
        <Select size="sm" value={tenantId} onChange={e => setTenantId(parseInt(e.target.value))}>
          {(tenants||[]).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </FormControl>

      {settings ? (
        <VStack spacing={4} align="stretch">
          <SimpleGrid columns={2} spacing={4}>
            <FormControl>
              <FormLabel fontSize="xs">Password Reset Expiry (minutes)</FormLabel>
              <NumberInput size="sm" min={5} max={1440} value={settings.password_reset_expiry_minutes || 60}
                onChange={v => set('password_reset_expiry_minutes', parseInt(v))}>
                <NumberInputField />
              </NumberInput>
              <Text fontSize="10px" color="gray.400" mt={0.5}>Default: 60 min. Max: 1440 (24h)</Text>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Session Duration (hours)</FormLabel>
              <NumberInput size="sm" min={1} max={720} value={settings.session_hours || 8}
                onChange={v => set('session_hours', parseInt(v))}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Max Login Attempts</FormLabel>
              <NumberInput size="sm" min={3} max={50} value={settings.max_login_attempts || 10}
                onChange={v => set('max_login_attempts', parseInt(v))}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Lockout Duration (minutes)</FormLabel>
              <NumberInput size="sm" min={1} max={1440} value={settings.lockout_minutes || 30}
                onChange={v => set('lockout_minutes', parseInt(v))}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
          </SimpleGrid>
          <HStack>
            <Switch isChecked={!!settings.require_2fa} size="sm"
              onChange={e => set('require_2fa', e.target.checked)} />
            <Text fontSize="13px">Require 2FA for all users in this tenant</Text>
          </HStack>
          <Button colorScheme="blue" size="sm" leftIcon={<FiSave size={13}/>}
            isLoading={saving} onClick={handleSave} alignSelf="flex-start">
            Save Settings
          </Button>
        </VStack>
      ) : <Spinner size="sm" />}
    </Box>
  );
}

// ── Main TAdminPage ───────────────────────────────────────────────────────────
export default function TAdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('tadmin_token'));
  const [admin, setAdmin] = useState(() => { try { return JSON.parse(sessionStorage.getItem('tadmin_admin')); } catch(e) { return null; } });
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [activity, setActivity] = useState([]);
  const [actFilter, setActFilter] = useState({ tenant_id: '', event_type: '' });
  const [loading, setLoading] = useState(false);
  const newTenantDisc = useDisclosure();
  const toast = useToast();

  const api = token ? tadminApi(token) : null;

  const handleLogin = (tok, adm) => {
    setToken(tok); setAdmin(adm);
    sessionStorage.setItem('tadmin_token', tok);
    sessionStorage.setItem('tadmin_admin', JSON.stringify(adm));
  };

  const handleLogout = () => {
    setToken(null); setAdmin(null);
    sessionStorage.removeItem('tadmin_token');
    sessionStorage.removeItem('tadmin_admin');
  };

  const loadAll = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [dashRes, tenantsRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/tenants'),
      ]);
      setDashboard(dashRes.data);
      setTenants(tenantsRes.data);
    } catch(e) {
      if (e.response?.status === 401) { handleLogout(); }
      else toast({ title: 'Failed to load data', status: 'error', duration: 2000 });
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line

  const loadActivity = useCallback(async () => {
    if (!api) return;
    try {
      const { data } = await api.get('/activity', actFilter);
      setActivity(data);
    } catch(e) {}
  }, [token, actFilter]); // eslint-disable-line

  const [migrating, setMigrating] = useState(false);
  const runMigrations = async () => {
    setMigrating(true);
    try {
      await api.post('/run-migrations', {});
      toast({ title: '✓ Migrations completed — reloading data', status: 'success', duration: 3000 });
      await loadAll();
    } catch(e) {
      toast({ title: 'Migration error: ' + (e.response?.data?.error || e.message), status: 'error', duration: 5000 });
    } finally { setMigrating(false); }
  };

  useEffect(() => { if (token) { loadAll(); loadActivity(); } }, [token]); // eslint-disable-line
  useEffect(() => { if (token) loadActivity(); }, [actFilter]); // eslint-disable-line

  if (!token) return <TAdminLogin onLogin={handleLogin} />;

  const dash = dashboard || {};

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Flex bg="gray.900" px={6} py={3} align="center" justify="space-between" boxShadow="md">
        <HStack spacing={3}>
          <FiShield size={20} color="#63b3ed" />
          <Text fontSize="16px" fontWeight="800" color="white">ForgeBOS T-Admin</Text>
          <Badge colorScheme="blue" fontSize="9px">Super Admin</Badge>
        </HStack>
        <HStack spacing={3}>
          <Text fontSize="12px" color="gray.400">{admin?.username}</Text>
          <Button size="xs" variant="outline" colorScheme="orange" color="orange.300"
            borderColor="orange.600" isLoading={migrating}
            onClick={runMigrations} title="Force-run database migrations">
            Run Migrations
          </Button>
          <IconButton icon={<FiRefreshCw size={14} />} size="sm" variant="ghost" color="gray.400"
            aria-label="Refresh" isLoading={loading} onClick={loadAll} />
          <Button size="sm" variant="ghost" color="gray.400" onClick={handleLogout}>Logout</Button>
        </HStack>
      </Flex>

      <Box maxW="1400px" mx="auto" p={6}>
        <Tabs colorScheme="blue" isLazy>
          <TabList mb={4}>
            <Tab fontSize="13px"><HStack><FiActivity size={14}/><Text>Dashboard</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiShield size={14}/><Text>Tenants</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiLogIn size={14}/><Text>Activity Log</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiSmartphone size={14}/><Text>My Security</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiMail size={14}/><Text>System Email</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiLock size={14}/><Text>Security Settings</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiGrid size={14}/><Text>Row Group Templates</Text></HStack></Tab>
            <Tab fontSize="13px"><HStack><FiTerminal size={14}/><Text>Debug</Text></HStack></Tab>
          </TabList>

          <TabPanels>
            {/* ── Dashboard tab ── */}
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                <StatCard label="Total Tenants" value={dash.tenantCount?.n ?? '—'}
                  sub={`${dash.tenantCount?.active ?? 0} active`} icon={FiShield} color="blue" />
                <StatCard label="Total Users" value={dash.userCount ?? '—'} icon={FiUsers} color="teal" />
                <StatCard label="Logins (30d)" value={dash.logins30d ?? dash.loginStats?.reduce((a,r)=>a+Number(r.logins_ok||0),0) ?? '—'}
                  icon={FiLogIn} color="green" />
                <StatCard label="Failed Logins (24h)"
                  value={dash.failedLogins24h ?? dash.failedByTenant?.reduce((a,r)=>a+Number(r.attempts||0),0) ?? '—'}
                  icon={FiAlertTriangle} color="red" />
              </SimpleGrid>

              {/* Failed logins in last 24h */}
              {dash.failedByTenant?.length > 0 && (
                <Box mb={6} bg="red.50" p={4} borderRadius="lg" border="1px" borderColor="red.200">
                  <Text fontSize="11px" fontWeight="700" color="red.700" textTransform="uppercase" mb={3}>
                    ⚠ Failed Login Attempts — Last 24 Hours
                  </Text>
                  <Table size="sm">
                    <Thead><Tr>
                      <Th>Tenant</Th><Th>Username</Th><Th isNumeric>Attempts</Th><Th>Last Attempt</Th>
                    </Tr></Thead>
                    <Tbody>
                      {dash.failedByTenant.map((r, i) => (
                        <Tr key={i}>
                          <Td fontSize="12px">{r.tenant_name || r.tenant_id}</Td>
                          <Td fontSize="12px" fontWeight="600">{r.username}</Td>
                          <Td isNumeric><Badge colorScheme={r.attempts >= 5 ? 'red' : 'orange'}>{r.attempts}</Badge></Td>
                          <Td fontSize="11px" color="gray.500">
                            {new Date(r.last_attempt).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}

              {/* Recent logins */}
              <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200" p={4}>
                <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={3}>
                  Recent Activity
                </Text>
                <ActivityTable rows={(dash.recentLogins || []).slice(0, 20)} />
              </Box>
            </TabPanel>

            {/* ── Tenants tab ── */}
            <TabPanel px={0}>
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="15px" fontWeight="700">Tenants ({tenants.length})</Text>
                <Button size="sm" colorScheme="blue" leftIcon={<FiPlus size={13}/>} onClick={newTenantDisc.onOpen}>
                  New Tenant
                </Button>
              </Flex>
              <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200" overflowX="auto">
                <Table size="sm">
                  <Thead bg="gray.50"><Tr>
                    <Th>Name</Th><Th>Slug</Th><Th>Status</Th><Th>Plan</Th>
                    <Th isNumeric>Users</Th><Th isNumeric>Projects</Th><Th>Last Login</Th><Th></Th>
                  </Tr></Thead>
                  <Tbody>
                    {tenants.map(t => (
                      <TenantRow key={t.id} tenant={t} api={api} onRefresh={loadAll} />
                    ))}
                    {tenants.length === 0 && (
                      <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.400">No tenants yet.</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
              <NewTenantModal isOpen={newTenantDisc.isOpen} onClose={newTenantDisc.onClose}
                api={api} onCreated={loadAll} />
            </TabPanel>

            {/* ── Activity log tab ── */}
            <TabPanel px={0}>
              <HStack mb={4} spacing={3}>
                <Select size="sm" w="200px" value={actFilter.tenant_id}
                  onChange={e => setActFilter(p => ({ ...p, tenant_id: e.target.value }))}>
                  <option value="">All Tenants</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
                <Select size="sm" w="160px" value={actFilter.event_type}
                  onChange={e => setActFilter(p => ({ ...p, event_type: e.target.value }))}>
                  <option value="">All Events</option>
                  <option value="login_success">Login Success</option>
                  <option value="login_fail">Login Fail</option>
                  <option value="password_reset">Password Reset</option>
                </Select>
                <Button size="sm" leftIcon={<FiRefreshCw size={12}/>} onClick={loadActivity}>Refresh</Button>
              </HStack>
              <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200" p={4}>
                <ActivityTable rows={activity} />
              </Box>
            </TabPanel>

            {/* ── My Security tab ── */}
            <TabPanel px={0}>
              <MfaSettingsPanel api={api} admin={admin} />
            </TabPanel>

            {/* ── System Email tab ── */}
            <TabPanel px={0}>
              <SystemEmailPanel api={api} />
            </TabPanel>

            {/* ── Security Settings tab ── */}
            <TabPanel px={0}>
              <SecuritySettingsPanel api={api} tenants={tenants} />
            </TabPanel>

            {/* ── Row Group Templates tab ── */}
            <TabPanel px={0}>
              <RowGroupTemplatesPanel api={api} tenants={tenants} />
            </TabPanel>

            {/* ── Debug tab ── */}
            <TabPanel px={0}>
              <DebugPanel token={token} />
            </TabPanel>

          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
